import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { DialogHeader, DialogTitle, DialogDescription, DialogContent } from '@/components/ui/dialog';
import { renderLogo } from 'src/utils/renderLogo';
import { Button } from '@/components/ui/button';
import { OauthIntegrationProviderEnum } from 'src/graphql/schema';
import { useIntegrationAuth } from 'src/hooks';
import { AzureForm, type IAzureForm, AzureDevOpsForm, type IAzureDevOpsForm } from 'src/components/Integration/Setup';
import { getProviderDisplayName } from 'src/utils/getProviderDisplayName';

import { useIntegration } from '../model';

const OauthIntegrationSetup = () => {
  const { provider, resetIntegration } = useIntegration();
  const { initAuth } = useIntegrationAuth(provider as OauthIntegrationProviderEnum);

  if (!provider) return null;

  const Icon = renderLogo(provider);
  const name = getProviderDisplayName(provider);

  const onAzureSubmit = async (data: IAzureForm | IAzureDevOpsForm) => {
    try {
      await initAuth(data);
      resetIntegration();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  return (
    <DialogContent className="md:max-w-xl gap-6">
      <DialogHeader className="text-center gap-6">
        <Icon className="size-10 mx-auto" />
        <DialogTitle className="text-xl font-bold">{name}</DialogTitle>
        <DialogDescription className="font-medium">
          Authorize Codekeeper to securely connect to your {name} account using OAuth. Once connected, you can choose
          which repositories to deposit, and Codekeeper will automatically monitor them for updates.
        </DialogDescription>
      </DialogHeader>

      {provider === 'azure' ? (
        <AzureForm onSubmit={onAzureSubmit} buttonText={`Connect to ${name}`} />
      ) : provider === 'azure_devops' ? (
        <AzureDevOpsForm onSubmit={onAzureSubmit} buttonText={`Connect to ${name}`} />
      ) : (
        <Button onClick={() => initAuth()} className="w-full" size="sm">
          Connect to {name} <ExternalLink />
        </Button>
      )}
    </DialogContent>
  );
};

export default OauthIntegrationSetup;
